import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { queryKeys } from '../api/queries';
import type { Deployment, RunSummary } from '../api/types';
import { planBlockers, type PlanBlocker } from '../lib/planReview';

/** The latest completed plan (one request: `kind=plan&status=completed&limit=1`), shared with the page's banner. */
export function useLatestPlan(deploymentId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.latestPlan(deploymentId),
    queryFn: async () =>
      (await api.listRunSummaries(deploymentId, { kind: 'plan', status: 'completed' }, { limit: 1 }))
        .items[0] ?? null,
    staleTime: 0,
  });
}

/** A plan that "Apply this plan" may bind to (spec 6.4): reviewable for the deployment now. */
export function reviewablePlan(
  plan: RunSummary | null | undefined,
  deployment: Deployment,
  latestVersion: number | undefined,
): RunSummary | null {
  return plan && planBlockers(plan, deployment, latestVersion).length === 0 ? plan : null;
}

/**
 * The latest completed plan when it is out of date only because a newer blueprint version exists or the settings
 * changed since (spec 6.4's out-of-date banner). A plan the deployment was applied after is history: null.
 */
export function outOfDatePlan(
  plan: RunSummary | null | undefined,
  deployment: Deployment,
  latestVersion: number | undefined,
): { plan: RunSummary; reasons: PlanBlocker[] } | null {
  if (!plan) return null;
  const reasons = planBlockers(plan, deployment, latestVersion);
  if (reasons.length === 0 || reasons.some((r) => r !== 'newerVersion' && r !== 'settingsChanged'))
    return null;
  return { plan, reasons };
}
